<script>
import axios from 'axios';
import truncate from 'truncate';
import Paginate from 'vuejs-paginate-next';

export default {
    props: {
        labels: String,
        categories: String,
        years: String,
        lang: String,
    },
    computed: {
        getCategoryFilter() {
            const category = this.parsedCategories.find((item) => item.id == this.axiosParams.categories);
            return category ? category.name : null;
        },
        getYearFilter() {
            return this.axiosParams.after !== '' ? this.axiosParams.after : null;
        },
    },
    mounted() {
        this.parsedLabels = JSON.parse(this.labels);
        this.parsedCategories = JSON.parse(this.categories);
        this.parsedYears = JSON.parse(this.years);
        this.axiosParams.lang = this.lang;

        const urlParams = new URLSearchParams(window.location.search);

        if (urlParams.has('c')) {
            this.showFilter = true;
            const c = urlParams.get('c');
            this.axiosParams.categories = c;
        }

        if (urlParams.has('y')) {
            this.showFilter = true;
            const year = urlParams.get('y');
            this.axiosParams.after = year;
        }

        this.getPosts();
    },
    data() {
        return {
            axiosUrl: '/wp-json/wp/v2/news',
            axiosParams: {
                per_page: 12,
                page: 1,
                search: '',
                categories: '',
                after: '',
                lang: ''
            },
            posts: [],
            pageCount: 1,
            showFilter: false,
            parsedLabels: [],
            parsedCategories: [],
            loading: false,
        };
    },
    components: {
        paginate: Paginate,
    },
    watch: {
        'axiosParams.categories'(newValue, oldValue) {
            if (newValue != oldValue) {
                this.resetAxiosParams();
                this.getPosts();
            }
        },
        'axiosParams.page'(newValue, oldValue) {
            if (newValue != oldValue) {
                this.getPosts();
            }
        },
        'axiosParams.after'(newValue, oldValue) {
            if (newValue != oldValue) {
                this.resetAxiosParams();
                this.getPosts();
            }
        },
    },
    methods: {
        decodeHtml(html) {
            const doc = new DOMParser().parseFromString(html, "text/html");
            return doc.documentElement.textContent;
        },
        getPosts() {
            this.loading = true;

            let params = Object.assign({}, this.axiosParams);

            if (params.categories === '') {
                delete params.categories;
            }

            if (params.after === '') {
                delete params.after;
            } else {
                const before = (+params.after + 1).toString();
                params.after = new Date(params.after).toISOString();
                params.before = new Date(before).toISOString();
            }

            axios
                .get(this.axiosUrl, { params: params })
                .then((response) => {
                    this.posts = response.data;
                    this.configPagination(response.headers);
                    setTimeout(() => {
                        this.loading = false;
                    }, 1000);
                    this.updateUrlParams();
                })
        },
        truncateString(string, limit) {
            return truncate(string, limit);
        },
        doPagination(page) {
            this.axiosParams.page = page;
        },
        formSubmit(e) {
            e.preventDefault();
            this.resetAxiosParams();
            this.getPosts();
        },
        configPagination(headers) {
            this.pageCount = +headers['x-wp-totalpages'];
        },
        resetAxiosParams() {
            this.axiosParams.page = 1;
        },
        resetCategoryFilter() {
            this.axiosParams.categories = '';
        },
        resetYearFilter() {
            this.axiosParams.after = '';
        },
        updateUrlParams() {
            const urlParams = new URLSearchParams(window.location.search);

            if (this.axiosParams.hasOwnProperty('categories')) {
                if (this.axiosParams.categories !== '') {
                    urlParams.set('c', this.axiosParams.categories);
                } else {
                    urlParams.delete('c');
                }
            }

            if (this.axiosParams.hasOwnProperty('after')) {
                if (this.axiosParams.after !== '') {
                    urlParams.set('y', this.axiosParams.after);
                } else {
                    urlParams.delete('y');
                }
            }

            window.history.pushState('', '', '?' + urlParams.toString());
        }
    },
};
</script>

<template>
    <form class="form" @submit="formSubmit">
        <fieldset>
            <legend><span class="sr-only">{{ parsedLabels.legend_search }}</span></legend>
            <input class="form__field" type="text" v-model="axiosParams.search" :aria-label="parsedLabels.search_label" />
            <button type="submit" class="button button--search" :aria-label="parsedLabels.search_button"></button>
            <button type="button" class="button button--filter" @click="showFilter = !showFilter">{{
                parsedLabels.filter_button
            }}</button>
        </fieldset>
        <fieldset v-if="showFilter">
            <legend><span class="sr-only">{{ parsedLabels.legend_filters }}</span></legend>
            <select class="form__select" v-model="axiosParams.categories">
                <option value disabled selected hidden>{{ parsedLabels.category_placeholder }}</option>
                <option v-for="item in parsedCategories" :value="item.id" :key="item.id">{{ item.name }}</option>
            </select>
            <select class="form__select" v-model="axiosParams.after">
                <option value disabled selected hidden>{{ parsedLabels.year_placeholder }}</option>
                <option v-for="item in parsedYears" :value="item" :key="item">{{ item }}</option>
            </select>
        </fieldset>
    </form>
    <div class="news-archive">
        <div class="active-filters">
            <strong v-if="getCategoryFilter || getYearFilter">{{ parsedLabels.filter_label }}:</strong>
            <button class="active-filters__button" @click="resetCategoryFilter()">{{ getCategoryFilter }}</button>
            <button class="active-filters__button" @click="resetYearFilter()">{{ getYearFilter }}</button>
        </div>
        <div v-if="loading" class="news-archive__loader">
            <div class="loader"></div>
        </div>
        <div v-if="!loading">
            <ul class="news-archive__list">
                <li class="news-item" v-bind:class="{ 'news-item--with-image': item.image }" v-for="item in posts"
                    :key="item.id">
                    <a :href="item.link" class="news-item__link">
                        <div v-if="item.image" class="news-item__image">
                            <img v-bind:src="item.image" v-bind:alt="decodeHtml(item.image_alt) || decodeHtml(item.title.rendered)" />
                        </div>
                        <div class="news-item__content">
                            <div v-if="item.category.name" class="news-item__tag">
                                <span class="tag tag--primary" :class="[`tag--${item.category.tag_color}`]">
                                    {{
                                        item.category.name
                                    }}
                                </span>
                            </div>
                            <span class="news-item__date">{{ item.custom_date.substr(0, 10) }}</span>
                            <h2 class="news-item__title" v-html="truncateString(item.title.rendered, (item.image ? 65 : 100))"></h2>
                        </div>
                    </a>
                </li>
            </ul>
            <p v-if="posts.length === 0">{{ parsedLabels.no_result }}</p>
        </div>
        <paginate v-if="pageCount > 1" :page-count="pageCount" :page-range="3" :margin-pages="1"
            :click-handler="doPagination" :prev-text="''" :next-text="''" :container-class="'pagination'"
            :page-class="'pagination__item'" :page-link-class="'pagination__item-link'"
            :next-class="'button button--next'" :prev-class="'button button--prev'"
            :disabled-class="'button--disabled'"></paginate>
    </div>
</template>

<style lang="scss" scoped>
.form {
    @include wrap(content);

    margin-bottom: 32px;

    @include media(M) {
        margin-bottom: 64px;
    }

    fieldset {
        display: flex;
        gap: 8px;
        margin-bottom: 8px;
    }

    input {
        width: 100%;
    }
}
</style>
